// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2026-01-14 17:43:36 +0100 (Wed, 14 Jan 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 159808 $
//  Purpose   : XMenu can dispatch to different backends (semantic-ui and contexify)
// =============================================================================
// ---Third-party imports
import React, {useRef} from 'react'
import {Menu, Icon, Label, Dropdown, Button, Input, Popup, Header, Modal} from 'semantic-ui-react'
import clsx from 'clsx'

// ---Framework imports
import {isObject} from 'ldash'
import {useEffect, useState} from 'uix/hooks'
import {VIcon} from 'uix/graphics'

// ---Local imports
import {XMenu} from './menu'
import {KeyboardShortCut} from './keyboardShortcuts'
import {Link} from './links'
import styles from './contextMenu.scss'

export function contextFromEvent(e) {
  const left = e.clientX
  const top = e.clientY
  const right = left + 1
  const bottom = top + 1

  return {
    getBoundingClientRect: () => ({
      left,
      top,
      right,
      bottom,
      height: 0,
      width: 0,
    }),
  }
}

const Group = ({
  animation,
  header,
  style,
  children,
  setOpen,
  setPopup,
  labelSize,
  headerLinkProps,
}) => {
  let headerEl = header
  if (isObject(header)) {
    headerEl = (
      <Label
        size={labelSize}
        style={style}
        basic
        title={header.title}
        className={styles.labelHeader}
        icon={header.icon}
        content={header.value}
      />
    )
  }
  if (isObject(headerLinkProps)) {
    headerEl = <Link {...headerLinkProps}>{headerEl}</Link>
  }
  return (
    <>
      {headerEl}
      {children && (
        <div className={styles.group}>
          {React.Children.map(children, child =>
            React.cloneElement(child, {animation: animation, setOpen: setOpen, setPopup: setPopup})
          )}
        </div>
      )}
    </>
  )
}

const Item = ({
  animation,
  children,
  icon,
  name,
  header,
  options,
  setPopup,
  setOpen,
  onClick,
  ...props
}) => {
  const onClickHandle = param => {
    param && param()
    setOpen ? setOpen(false) : setPopup ? setPopup(false) : null
  }
  if (options) {
    return (
      <Dropdown
        item
        trigger={
          <span>
            <VIcon name={icon} style={{marginRight: '0.5rem'}} />
            <span>{name}</span>
          </span>
        }
        {...props}
      >
        <Dropdown.Menu
          className={`transition ${animation} ${open ? 'in' : 'out'}`}
          style={{borderTop: 'none'}}
        >
          {header && <Dropdown.Header icon={header.icon} content={header.value} />}
          {options && (
            <Dropdown.Menu scrolling>
              {options.map(opt => {
                let {func, value, ...o} = opt
                return (
                  <Dropdown.Item
                    text={value}
                    key={value}
                    onClick={() => onClickHandle(func)}
                    {...o}
                  />
                )
              })}
            </Dropdown.Menu>
          )}
        </Dropdown.Menu>
      </Dropdown>
    )
  } else {
    return (
      <XMenu.Item style={{textTransform: 'none'}} onClick={() => onClickHandle(onClick)} {...props}>
        <VIcon name={icon} />
        {name}
      </XMenu.Item>
    )
  }
}

export const ContextMenu = ({
  animation = 'slide down',
  children,
  content,
  context = undefined,
  delay = false,
  disableClick,
  dropdown = false,
  header,
  setOpen = undefined,
  open = undefined,
  options,
  quickOptions,
  rightClick = false,
  closeOnEscape = true,
  size = null,
  trigger,
}) => {
  const [timeout, setTimeoutHandle] = useState(delay)
  // this is ther inner onOpen tracking
  const [popup, setPopup] = useState(false)
  const ref = useRef(null)

  if (delay === true) {
    //default mouseHover delay
    delay = '1800'
  }
  const setOpenState = value => {
    setOpen ? setOpen(value) : setPopup(value)
  }
  let useOutsideClick = ref =>
    useEffect(() => {
      let handleClickOutside = event => {
        if (ref.current && !ref.current.contains(event.target)) {
          setOpenState(false)
        }
      }
      // Bind the event listener
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        // Unbind the event listener on clean up
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [ref])

  useOutsideClick(ref)

  const handleMouseEnter = () => {
    if (delay) {
      setTimeoutHandle(
        setTimeout(() => {
          setOpenState(true)
        }, delay)
      )
    }
  }

  const handleMouseLeave = () => {
    clearTimeout(timeout)
  }

  const handleClick = e => {
    e.preventDefault()
    if (e.type === 'contextmenu' && rightClick) {
      setOpenState(true)
    }
    if (e.type === 'click' && rightClick) {
      handleClose()
    } else {
      if (e.type === 'click') {
        setOpenState(true)
      }
    }
  }

  const handleClose = () => {
    setOpenState(false)
  }

  const onClickHandle = param => {
    param && param()
    handleClose()
  }

  let labelSize = {
    mini: 'mini',
    tiny: 'mini',
    small: 'tiny',
    default: 'small',
    large: 'default',
  }[size]

  let quickbar = {
    display: 'flex',
    position: 'relative',
    margin: '0',
    right: '0',
    left: '0',
    top: '0',
  }

  let animationClass = null
  if (animation) {
    animationClass = `transition ${animation} ${open ? 'in' : 'out' || popup ? 'in' : 'out'}`
  }

  if (dropdown) {
    return (
      <div className={styles.contextMenu} ref={ref}>
        <Dropdown
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={disableClick ? null : handleClick}
          onClose={handleClose}
          onContextMenu={handleClick}
          trigger={trigger}
          icon={false}
          open={popup}
        >
          <Dropdown.Menu>
            {quickOptions && (
              <div className={clsx('menu', styles.quickbar)} style={quickbar}>
                {quickOptions.map(opt => {
                  let {func, value, ...o} = opt
                  return <Dropdown.Item key={value} onClick={() => onClickHandle(func)} {...o} />
                })}
              </div>
            )}
            {header && <Dropdown.Header icon={header.icon} content={header.value} />}
            {options && (
              <Dropdown.Menu scrolling>
                {options.map(opt => {
                  let {func, value, ...o} = opt
                  return (
                    <Dropdown.Item
                      text={value}
                      key={value}
                      onClick={() => onClickHandle(func)}
                      {...o}
                    />
                  )
                })}
              </Dropdown.Menu>
            )}
          </Dropdown.Menu>
        </Dropdown>
      </div>
    )
  }

  return (
    <Popup
      open={open || popup}
      context={context}
      className={clsx(styles.contextMenu, styles.popup, animationClass)}
      basic
      hideOnScroll={context ? true : true}
      style={{padding: ' 0'}}
      trigger={
        <div
          style={{maxWidth: 'fit-content'}}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onContextMenu={handleClick}
          onClick={disableClick ? null : handleClick}
          as="button"
        >
          {trigger}
        </div>
      }
    >
      <KeyboardShortCut name="Escape" onTrigger={e => setOpenState(false)} />
      <Popup.Content onClick={e => e.stopPropagation()}>
        <div ref={ref}>
          {quickOptions && (
            <XMenu size={size} borderless className={styles.quickbar} style={quickbar}>
              {quickOptions.map(opt => {
                let {func, value, ...o} = opt
                return (
                  <XMenu.Item
                    // content={opt.value}
                    key={value}
                    onClick={() => onClickHandle(func)}
                    {...o}
                  />
                )
              })}
            </XMenu>
          )}
          {header && (
            <Label
              size={labelSize}
              style={{textTransform: 'uppercase', border: '0px'}}
              basic
              className={styles.labelHeader}
              icon={header.icon}
              content={header.value}
            />
          )}
          {options && (
            <XMenu style={{marginTop: '0'}} borderless size={size} vertical>
              {options.map(opt => {
                let {func, value, ...o} = opt
                return (
                  <XMenu.Item
                    content={value}
                    key={value}
                    onClick={() => onClickHandle(func)}
                    {...o}
                  />
                )
              })}
            </XMenu>
          )}
          {content && content}
          {children && (
            <XMenu style={{marginTop: '0'}} borderless size={size} vertical>
              {React.Children.map(children, child =>
                React.cloneElement(child, {
                  setPopup: setPopup,
                  setOpen: setOpen,
                  animation: animation,
                  labelSize: labelSize,
                })
              )}
            </XMenu>
          )}
        </div>
      </Popup.Content>
    </Popup>
  )
}

// ---Exports
ContextMenu.Item = Item
ContextMenu.Group = Group
